import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useState } from 'react';
import { useCurrentUserProfile, useWaysteClient } from '@alliance-disposal/client';
import { useSourContext } from '@wayste/sour-context';
import { isAdminPortalTicket } from './utils';
// Create typed context
const InternalTicketContext = createContext(null);
export const InternalTicketProvider = ({ children }) => {
    const client = useWaysteClient();
    const { application, setShowToast } = useSourContext();
    const { profile: user } = useCurrentUserProfile();
    // Get the correct service based on application
    const ticketService = application === 'admin-portal' ? client.internalTicket().adminPortal : client.internalTicket().wayste;
    const [state, setState] = useState(() => ({
        tickets: [],
        allTickets: [],
        isLoading: false,
        entityID: '',
        entityType: undefined,
        filters: {
            showArchived: false,
            onlyMine: false,
            tagIDs: [],
            statuses: [],
            teams: [],
            visibility: [],
        },
        ticketStatusCounts: {
            TOTAL: 0,
            MY_TASKS: 0,
            NOTES: 0,
            TODO: 0,
            IN_PROGRESS: 0,
            DONE: 0,
            IMAGES: 0,
        },
        allTags: [],
        allTagsOnTickets: [],
        replyVisibility: {
            allExpanded: true,
            expandedIDs: new Set(),
        },
    }));
    const getTagsWithCount = (tickets) => {
        // Get all tags from both tickets and replies
        const usedTags = tickets.flatMap((ticket) => [
            ...(ticket.tags || []),
            ...(ticket.replies?.flatMap((reply) => reply.tags || []) || []),
        ]);
        // Count occurrences of each tag
        return usedTags.reduce((acc, tag) => {
            const found = acc.find((t) => t.tag.id === tag.id);
            if (found) {
                found.count++;
            }
            else {
                acc.push({ tag, count: 1 });
            }
            return acc;
        }, []);
    };
    const getStatusCounts = (tickets) => {
        // Get all tickets and their replies flattened into one array
        const allTicketsAndReplies = tickets.reduce((acc, ticket) => {
            if (!state.filters.showArchived && (ticket.deletedAt || ticket.archivedDate))
                return acc;
            acc.push(ticket);
            if (ticket.replies?.length && state.replyVisibility.expandedIDs.has(ticket.id)) {
                ticket.replies.forEach((reply) => {
                    if (!state.filters.showArchived && (reply.deletedAt || reply.archivedDate))
                        return;
                    acc.push(reply);
                });
            }
            return acc;
        }, []);
        return {
            TOTAL: allTicketsAndReplies.length,
            MY_TASKS: allTicketsAndReplies.filter((ticket) => ticket.assignedToProfileID === user?.id).length,
            NOTES: allTicketsAndReplies.filter((ticket) => !ticket.assignedToProfileID).length,
            TODO: allTicketsAndReplies.filter((ticket) => ticket.status === 'OPEN').length,
            IN_PROGRESS: allTicketsAndReplies.filter((ticket) => ticket.status === 'IN_PROGRESS').length,
            DONE: allTicketsAndReplies.filter((ticket) => ticket.status === 'SOLVED').length,
            IMAGES: allTicketsAndReplies.filter((ticket) => ticket.images?.length > 0).length,
        };
    };
    const handleTicketResponse = (response) => {
        const statusCounts = getStatusCounts(response.results);
        const tagsWithCount = getTagsWithCount(response.results);
        setState((s) => ({
            ...s,
            allTickets: response.results,
            ticketStatusCounts: statusCounts,
            allTagsOnTickets: tagsWithCount,
            replyVisibility: {
                allExpanded: true,
                expandedIDs: new Set(response.results.map((t) => t.id)),
            },
        }));
    };
    const fetchTags = async (query) => {
        const response = await ticketService.tag.query({ limit: 100, ...query });
        setState((s) => ({ ...s, allTags: response.results }));
        return response.results;
    };
    const fetchInternalTickets = async (query) => {
        if (!state.entityID) {
            setShowToast({
                message: 'Missing entity ID',
                severity: 'error',
            });
            return;
        }
        try {
            const response = await ticketService.query({
                limit: 100,
                entityID: state.entityID,
                ...query,
            });
            handleTicketResponse(response);
        }
        catch (error) {
            console.error('Error fetching internal tickets:', error);
            setShowToast({
                message: 'An error occurred fetching internal tickets',
                severity: 'error',
            });
        }
    };
    // Filter effect
    useEffect(() => {
        let filteredTickets = state.allTickets.map((ticket) => ({
            ...ticket,
            replies: ticket.replies ? [...ticket.replies] : [],
        }));
        if (state.filters.teams.length > 0) {
            if (application !== 'admin-portal')
                return;
            filteredTickets = filteredTickets.filter((ticket) => {
                // First filter the replies
                if (ticket.replies?.length) {
                    ticket.replies = ticket.replies.filter((reply) => isAdminPortalTicket(application, reply) &&
                        reply.assignedTeam &&
                        state.filters.teams.includes(reply.assignedTeam));
                }
                // Then check if either the parent ticket matches or if it has any matching replies
                const parentMatches = isAdminPortalTicket(application, ticket) && ticket.assignedTeam && state.filters.teams.includes(ticket.assignedTeam);
                return parentMatches || ticket.replies?.length > 0;
            });
        }
        if (state.filters.tagIDs.length > 0) {
            filteredTickets = filteredTickets.filter((ticket) => {
                // First filter the replies
                if (ticket.replies?.length) {
                    ticket.replies = ticket.replies.filter((reply) => reply.tags?.some((tag) => state.filters.tagIDs.includes(tag.id)));
                }
                // Then check if either the parent ticket matches or if it has any matching replies
                const parentMatches = ticket.tags?.some((tag) => state.filters.tagIDs.includes(tag.id));
                return parentMatches || ticket.replies?.length > 0;
            });
        }
        if (!state.filters.showArchived) {
            filteredTickets = filteredTickets.filter((ticket) => {
                // First filter the replies
                if (ticket.replies?.length) {
                    ticket.replies = ticket.replies.filter((reply) => !reply.archivedDate && !reply.deletedAt);
                }
                // Only keep parent ticket if it's not archived/deleted and either:
                // 1. It matches the filter criteria itself, or
                // 2. It has replies that match the criteria
                const parentMatches = !ticket.archivedDate && !ticket.deletedAt;
                return parentMatches && (ticket.replies?.length > 0 || !ticket.replies?.length);
            });
        }
        if (state.filters.onlyMine) {
            filteredTickets = filteredTickets.filter((ticket) => {
                // First filter the replies
                if (ticket.replies?.length) {
                    ticket.replies = ticket.replies.filter((reply) => reply.assignedToProfileID === user?.id);
                }
                // Then check if either the parent ticket matches or if it has any matching replies
                const parentMatches = ticket.assignedToProfileID === user?.id;
                return parentMatches || ticket.replies?.length > 0;
            });
        }
        if (state.filters.visibility.length > 0) {
            filteredTickets = filteredTickets.filter((ticket) => {
                // First filter the replies
                if (ticket.replies?.length) {
                    ticket.replies = ticket.replies.filter((reply) => reply.visibility.some((v) => state.filters.visibility.includes(v.key)));
                }
                // Then check if either the parent ticket matches or if it has any matching replies
                const parentMatches = ticket.visibility.some((v) => state.filters.visibility.includes(v.key));
                return parentMatches || ticket.replies?.length > 0;
            });
        }
        const statusCounts = getStatusCounts(filteredTickets);
        // Filters not affecting counts
        if (state.filters.statuses.length > 0) {
            filteredTickets = filteredTickets.filter((ticket) => {
                // First filter the replies
                if (ticket.replies?.length) {
                    ticket.replies = ticket.replies.filter((reply) => (reply.status && state.filters.statuses.includes(reply.status)) ||
                        (state.filters.statuses.includes('NOTES') && !reply.assignedToProfileID) ||
                        (state.filters.statuses.includes('IMAGES') && reply.images?.length > 0));
                }
                // Then check if either the parent ticket matches or if it has any matching replies
                const parentMatches = (ticket.status && state.filters.statuses.includes(ticket.status)) ||
                    (state.filters.statuses.includes('NOTES') && !ticket.assignedToProfileID) ||
                    (state.filters.statuses.includes('IMAGES') && ticket.images?.length > 0);
                return parentMatches || ticket.replies?.length > 0;
            });
        }
        const tagsWithCount = getTagsWithCount(filteredTickets);
        setState((s) => ({ ...s, tickets: filteredTickets, ticketStatusCounts: statusCounts, allTagsOnTickets: tagsWithCount }));
    }, [state.filters, state.allTickets, state.replyVisibility]);
    useEffect(() => {
        if (!state.entityID) {
            return;
        }
        setState((s) => ({ ...s, isLoading: true }));
        // This is a bit of a hack to get the subscription working, doesn't like it being unified for some reason
        let sub;
        if (application === 'admin-portal') {
            sub = client.internalTicket().adminPortal.subscription.query({ entityID: state.entityID });
            sub.receive.subscribe({
                next: (response) => {
                    handleTicketResponse(response);
                    setState((s) => ({ ...s, isLoading: false }));
                },
                error: (error) => {
                    console.error(error);
                    setShowToast({
                        message: 'Error in ticket subscription',
                        severity: 'error',
                    });
                },
            });
        }
        else {
            sub = client.internalTicket().wayste.subscription.query({ entityID: state.entityID });
            sub.receive.subscribe({
                next: (response) => {
                    handleTicketResponse(response);
                    setState((s) => ({ ...s, isLoading: false }));
                },
                error: (error) => {
                    console.error(error);
                    setShowToast({
                        message: 'Error in ticket subscription',
                        severity: 'error',
                    });
                },
            });
        }
        return () => {
            sub?.unsubscribe();
        };
    }, [state.entityID]);
    const value = {
        ...state,
        fetchInternalTickets,
        fetchTags,
        setEntityID: (id) => setState((s) => ({ ...s, entityID: id })),
        setEntityType: (type) => setState((s) => ({ ...s, entityType: type })),
        setFilters: (filters) => setState((s) => ({ ...s, filters })),
        handleArchive: async (id, archive) => {
            const updatedTicket = await ticketService.update(id, {
                archivedDate: archive ? new Date().toISOString() : null,
            });
            await fetchInternalTickets();
            return updatedTicket;
        },
        createTicket: ticketService.create,
        updateTicket: ticketService.update,
        deleteTicket: ticketService.delete,
        toggleReplies: (ticketId) => {
            setState((s) => ({
                ...s,
                replyVisibility: ticketId
                    ? {
                        // When toggling individual ticket
                        allExpanded: !s.replyVisibility.expandedIDs.has(ticketId) &&
                            s.replyVisibility.expandedIDs.size + 1 === state.allTickets.length,
                        expandedIDs: s.replyVisibility.expandedIDs.has(ticketId)
                            ? new Set([...s.replyVisibility.expandedIDs].filter((id) => id !== ticketId))
                            : new Set([...s.replyVisibility.expandedIDs, ticketId]),
                    }
                    : {
                        // When toggling all replies
                        allExpanded: !s.replyVisibility.allExpanded,
                        expandedIDs: !s.replyVisibility.allExpanded ? new Set(state.allTickets.map((t) => t.id)) : new Set(), // Clear the set when collapsing all
                    },
            }));
        },
        areRepliesShown: (ticketId) => state.replyVisibility.allExpanded || (ticketId && state.replyVisibility.expandedIDs.has(ticketId)),
    };
    return _jsx(InternalTicketContext.Provider, { value: value, children: children });
};
export { InternalTicketContext };
//# sourceMappingURL=context.js.map